<template>
  <div class="permission-matrix">
    <div class="pm-toolbar">
      <div class="pm-title">
        <h5 v-if="title">{{ title }}</h5>
        <span class="pm-count" :class="{ 'is-active': selectedCount > 0 }">
          {{ selectedCount }} / {{ allPermissionIds.length }} selected
        </span>
      </div>
      <div class="pm-actions">
        <div class="pm-search">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input v-model.trim="search" type="search" class="form-control form-control-sm" placeholder="Search permissions..." />
        </div>
        <button type="button" class="btn btn-sm pm-btn" :disabled="!allPermissionIds.length || allSelected" @click="setIds(allPermissionIds, true)">
          Select all
        </button>
        <button type="button" class="btn btn-sm pm-btn" :disabled="!selectedCount" @click="emitSelection([])">
          Clear
        </button>
      </div>
    </div>

    <div v-if="!permissions.length" class="pm-empty">No permissions available.</div>
    <div v-else-if="!visibleSections.length" class="pm-empty">No permissions match "{{ search }}".</div>

    <div v-else class="pm-table-wrap">
      <table class="pm-table">
        <thead>
          <tr>
            <th class="pm-col-name">Module</th>
            <th v-for="action in standardActions" :key="action.key" class="pm-col-action">
              <label class="pm-head-check" :title="`Toggle ${action.label} for all listed modules`">
                <input
                  type="checkbox"
                  :checked="columnState(action.key) === 'all'"
                  :indeterminate="columnState(action.key) === 'some'"
                  :disabled="columnState(action.key) === 'none-available'"
                  @change="setIds(columnIds(action.key), $event.target.checked)"
                />
                <span>{{ action.label }}</span>
              </label>
            </th>
            <th class="pm-col-extra">Other</th>
            <th class="pm-col-all">All</th>
          </tr>
        </thead>

        <tbody v-for="section in visibleSections" :key="section.module">
          <tr v-if="section.showHeader" class="pm-section-row">
            <td :colspan="standardActions.length + 3">
              <label class="pm-section-check">
                <input
                  type="checkbox"
                  :checked="stateOf(section.ids) === 'all'"
                  :indeterminate="stateOf(section.ids) === 'some'"
                  @change="setIds(section.ids, $event.target.checked)"
                />
                <span>{{ section.label }}</span>
                <small>{{ countSelected(section.ids) }}/{{ section.ids.length }}</small>
              </label>
            </td>
          </tr>

          <tr v-for="row in section.rows" :key="row.key" class="pm-row" :class="{ 'is-nested': section.showHeader }">
            <td class="pm-col-name">
              <span class="pm-row-label">{{ row.label }}</span>
            </td>

            <td v-for="action in standardActions" :key="action.key" class="pm-col-action">
              <input
                v-if="row.cells[action.key]"
                type="checkbox"
                class="pm-cell-check"
                :title="row.cells[action.key].description || row.cells[action.key].permission_key"
                :aria-label="`${row.label}: ${action.label}`"
                :checked="isSelected(row.cells[action.key].permissionid)"
                @change="setIds([row.cells[action.key].permissionid], $event.target.checked)"
              />
              <span v-else class="pm-na">&mdash;</span>
            </td>

            <td class="pm-col-extra">
              <div v-if="row.extras.length" class="pm-chips">
                <button
                  v-for="extra in row.extras"
                  :key="extra.permission.permissionid"
                  type="button"
                  class="pm-chip"
                  :class="{ 'is-on': isSelected(extra.permission.permissionid) }"
                  :title="extra.permission.description || extra.permission.permission_key"
                  :aria-pressed="isSelected(extra.permission.permissionid)"
                  @click="setIds([extra.permission.permissionid], !isSelected(extra.permission.permissionid))"
                >
                  <i class="fa" :class="isSelected(extra.permission.permissionid) ? 'fa-check' : 'fa-plus'" aria-hidden="true"></i>
                  {{ extra.label }}
                </button>
              </div>
              <span v-else class="pm-na">&mdash;</span>
            </td>

            <td class="pm-col-all">
              <input
                type="checkbox"
                class="pm-cell-check"
                :aria-label="`All ${row.label} permissions`"
                :checked="stateOf(row.ids) === 'all'"
                :indeterminate="stateOf(row.ids) === 'some'"
                @change="setIds(row.ids, $event.target.checked)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="pm-hint">Tip: hover over a checkbox to see what the permission allows.</p>
  </div>
</template>

<script>
import { formatModuleLabel } from '@/utils/accessControl';

const STANDARD_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'add', label: 'Add' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
];
const STANDARD_KEYS = new Set(STANDARD_ACTIONS.map((action) => action.key));

// Splits "reports.sales.export" into resource "reports.sales" and action "export".
function parseKey(permissionKey, module) {
  const parts = String(permissionKey || '').split('.');
  if (parts.length < 2) {
    return { resource: module, action: parts[0] || '' };
  }
  return { resource: parts.slice(0, -1).join('.'), action: parts[parts.length - 1] };
}

function buildRows(group) {
  const rows = new Map();
  for (const permission of group.permissions) {
    const { resource, action } = parseKey(permission.permission_key, group.module);
    if (!rows.has(resource)) {
      const subResource = resource.startsWith(`${group.module}.`) ? resource.slice(group.module.length + 1) : '';
      rows.set(resource, {
        key: resource,
        label: subResource ? formatModuleLabel(subResource.replace(/\./g, ' ')) : group.label,
        cells: {},
        extras: [],
        ids: [],
        permissions: [],
      });
    }
    const row = rows.get(resource);
    if (STANDARD_KEYS.has(action) && !row.cells[action]) {
      row.cells[action] = permission;
    } else {
      row.extras.push({ label: formatModuleLabel(action), permission });
    }
    row.ids.push(String(permission.permissionid));
    row.permissions.push(permission);
  }
  return Array.from(rows.values());
}

export default {
  name: 'PermissionMatrix',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    permissions: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      search: '',
      standardActions: STANDARD_ACTIONS,
    };
  },
  computed: {
    selectedSet() {
      return new Set((this.modelValue || []).map(String));
    },
    allPermissionIds() {
      return this.permissions.flatMap((group) => group.permissions.map((permission) => String(permission.permissionid)));
    },
    selectedCount() {
      return this.countSelected(this.allPermissionIds);
    },
    allSelected() {
      return this.allPermissionIds.length > 0 && this.selectedCount === this.allPermissionIds.length;
    },
    sections() {
      return this.permissions.map((group) => ({
        module: group.module,
        label: group.label,
        rows: buildRows(group),
      }));
    },
    visibleSections() {
      const term = this.search.toLowerCase();
      return this.sections
        .map((section) => {
          const rows = term
            ? section.rows.filter((row) =>
                [section.label, row.label, ...row.permissions.flatMap((p) => [p.permission_key, p.description])]
                  .some((text) => String(text || '').toLowerCase().includes(term))
              )
            : section.rows;
          return {
            ...section,
            rows,
            ids: rows.flatMap((row) => row.ids),
            showHeader: section.rows.length > 1,
          };
        })
        .filter((section) => section.rows.length);
    },
    visibleRows() {
      return this.visibleSections.flatMap((section) => section.rows);
    },
  },
  methods: {
    emitSelection(values) {
      this.$emit('update:modelValue', [...new Set(values.map(String))]);
    },
    setIds(ids, checked) {
      const next = new Set(this.selectedSet);
      for (const id of ids) {
        if (checked) {
          next.add(String(id));
        } else {
          next.delete(String(id));
        }
      }
      this.emitSelection(Array.from(next));
    },
    isSelected(id) {
      return this.selectedSet.has(String(id));
    },
    countSelected(ids) {
      return ids.filter((id) => this.selectedSet.has(String(id))).length;
    },
    stateOf(ids) {
      if (!ids.length) {
        return 'none-available';
      }
      const count = this.countSelected(ids);
      if (count === 0) return 'none';
      return count === ids.length ? 'all' : 'some';
    },
    columnIds(actionKey) {
      return this.visibleRows
        .filter((row) => row.cells[actionKey])
        .map((row) => String(row.cells[actionKey].permissionid));
    },
    columnState(actionKey) {
      return this.stateOf(this.columnIds(actionKey));
    },
  },
};
</script>

<style scoped>
.permission-matrix { display:flex; flex-direction:column; gap:12px; }

.pm-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.pm-title { display:flex; align-items:center; gap:10px; }
.pm-title h5 { margin:0; font-size:1rem; font-weight:600; }
.pm-count { font-size:.78rem; padding:3px 10px; border-radius:999px; background:#f3f4f6; color:#6b7280; font-weight:500; white-space:nowrap; }
.pm-count.is-active { background:#fff4e8; color:#c26a12; }
.pm-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.pm-search { position:relative; }
.pm-search i { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; font-size:.8rem; }
.pm-search input { padding-left:28px; min-width:220px; height:32px; }
.pm-btn { border:1px solid #e5e7eb; background:#fff; color:#374151; height:32px; }
.pm-btn:hover:not(:disabled) { border-color:#FF9F43; color:#FF9F43; }

.pm-empty { padding:16px; border:1px dashed #d1d5db; border-radius:10px; color:#6b7280; font-size:.9rem; text-align:center; }

.pm-table-wrap { border:1px solid #e5e7eb; border-radius:10px; overflow:auto; max-height:520px; }
.pm-table { width:100%; border-collapse:separate; border-spacing:0; font-size:.875rem; }
.pm-table thead th { position:sticky; top:0; z-index:1; background:#f9fafb; border-bottom:1px solid #e5e7eb; padding:10px 12px; font-weight:600; color:#374151; white-space:nowrap; }
.pm-table td { padding:8px 12px; border-bottom:1px solid #f1f2f4; vertical-align:middle; }
.pm-table tbody:last-child tr:last-child td { border-bottom:none; }

.pm-col-name { text-align:left; min-width:160px; }
.pm-col-action, .pm-col-all { text-align:center; width:76px; }
.pm-col-extra { text-align:left; min-width:160px; }
.pm-col-all { border-left:1px solid #f1f2f4; }

.pm-head-check { display:inline-flex; flex-direction:column; align-items:center; gap:4px; margin:0; cursor:pointer; font-weight:600; }

.pm-row:hover td { background:#fffaf5; }
.pm-row-label { color:#111827; font-weight:500; }
.pm-row.is-nested .pm-col-name { padding-left:30px; }
.pm-row.is-nested .pm-row-label { font-weight:400; color:#374151; }

.pm-section-row td { background:#fcfcfd; padding:8px 12px; }
.pm-section-check { display:inline-flex; align-items:center; gap:8px; margin:0; cursor:pointer; font-weight:600; color:#111827; }
.pm-section-check small { color:#9ca3af; font-weight:400; }

.pm-table input[type="checkbox"] { width:16px; height:16px; cursor:pointer; accent-color:#FF9F43; margin:0; }
.pm-table input[type="checkbox"]:disabled { cursor:not-allowed; opacity:.4; }
.pm-na { color:#d1d5db; }

.pm-chips { display:flex; flex-wrap:wrap; gap:6px; }
.pm-chip { display:inline-flex; align-items:center; gap:5px; padding:2px 10px; border-radius:999px; border:1px solid #e5e7eb; background:#fff; color:#6b7280; font-size:.78rem; line-height:1.6; cursor:pointer; transition:all .15s ease; }
.pm-chip i { font-size:.65rem; }
.pm-chip:hover { border-color:#FF9F43; color:#FF9F43; }
.pm-chip.is-on { background:#FF9F43; border-color:#FF9F43; color:#fff; }

.pm-hint { margin:0; font-size:.75rem; color:#9ca3af; }

@media (max-width: 575px) {
  .pm-search input { min-width:0; width:100%; }
  .pm-search { flex:1 1 100%; }
}
</style>
